import {AnimatePresence, motion} from 'framer-motion';

import {CSSProperties, useState} from 'react';

import {isNil} from 'ramda';

import {suffixTestId} from 'shared/web';

import {useFoundationTheme} from '../../hooks/useFoundationTheme';
import {useResponsivePropValue} from '../../hooks/useResponsivePropValue';
import {ImageProps} from '../../types/ImageProps';
import {SpinnerColor} from '../../types/SpinnerColor';
import {DefaultFoundationTheme} from '../../utils/foundationTheme';
import {getCssSize} from '../../utils/getCssSize';
import {Box} from '../Box/Box';
import {Center} from '../Center/Center';
import {Icon} from '../Icon/Icon';
import {Show} from '../Show/Show';
import {Spinner} from '../Spinner/Spinner';

export type {ImageFit} from '../../types/ImageFit';
export type {ImageProps} from '../../types/ImageProps';

export function Image(props: ImageProps) {
  const theme = useFoundationTheme();
  const [isLoading, setLoading] = useState<boolean>(true);
  const [isError, setError] = useState<boolean>(false);

  const width = useResponsivePropValue(props.width);
  const height = useResponsivePropValue(props.height);
  const minWidth = useResponsivePropValue(props.minWidth);
  const minHeight = useResponsivePropValue(props.minHeight);
  const maxWidth = useResponsivePropValue(props.maxWidth);
  const maxHeight = useResponsivePropValue(props.maxHeight);

  const imageStyle: CSSProperties = {
    width: getCssSize(width) ?? '100%',
    height: getCssSize(height) ?? '100%',
    minWidth: getCssSize(minWidth) ?? 'initial',
    minHeight: getCssSize(minHeight) ?? 'initial',
    maxWidth: getCssSize(maxWidth) ?? 'initial',
    maxHeight: getCssSize(maxHeight) ?? 'initial',
    objectFit: props.fit,
    objectPosition: props.position,
    aspectRatio: props.ratio,
    opacity: isNil(props.src) || isLoading || isError ? 0 : 1,
  };

  return (
    <Box
      width={width}
      height={height}
      minWidth={minWidth ?? 'initial'}
      minHeight={minHeight ?? 'initial'}
      maxWidth={maxWidth ?? 'initial'}
      maxHeight={maxHeight ?? 'initial'}
      position="relative"
      overflow="hidden"
      ratio={props.ratio}
      borderRadius={props.borderRadius}
    >
      <img
        data-testid={suffixTestId('image', props)}
        style={imageStyle}
        alt={props.alt}
        draggable={props.draggable ?? undefined}
        src={props.src ?? undefined}
        loading={props.isLazy ? 'lazy' : undefined}
        onLoad={() => {
          setLoading(false);
          props.onLoad?.();
        }}
        onError={() => {
          setLoading(false);
          setError(true);
          props.onError?.();
        }}
      />
      <Show when={isNil(props.src) || isError}>
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          backgroundColor={theme.components.Image.emptyFill}
        >
          <Center width="100%" height="100%">
            <Icon color="palettes.neutral.70.100" value="image/photo" size={6} />
          </Center>
        </Box>
      </Show>
      <AnimatePresence mode="wait">
        {renderSpinner(isLoading && props.hasSpinner === true, props.spinnerColor, theme)}
      </AnimatePresence>
    </Box>
  );
}

function renderSpinner(
  isVisible: boolean,
  spinnerColor: SpinnerColor | undefined,
  theme: DefaultFoundationTheme
) {
  if (!isVisible) {
    return null;
  }

  return (
    <motion.div
      initial={{opacity: 1}}
      animate={{opacity: 1}}
      exit={{opacity: 0}}
      transition={{duration: 0.3}}
      style={{
        backgroundColor: theme.colors.palettes.white[10][100],
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}
    >
      <Center width="100%" height="100%">
        <Spinner color={spinnerColor} />
      </Center>
    </motion.div>
  );
}
